import { Injectable, Injector } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { StatusCodes } from '../utils/status.codes';
import { MessageService } from 'primeng';
import { Message } from '../classes/Message';
import * as primeNg from 'primeng';
import { ConfirmationService } from 'primeng';

export interface IHttpResponsesHandler {
  handleSuccess?: (message?: string) => void;
  handleError?: (errorResponse: HttpErrorResponse) => boolean;
}

export class BaseHttpResponsesHandler implements IHttpResponsesHandler {
  constructor(protected injector: Injector) {}

  handleSuccess(message?: string) {
    // not implemented
  }

  handleError(errorResponse: HttpErrorResponse): boolean {
    console.log(errorResponse.status);
    switch (errorResponse.status) {
      case StatusCodes.Status400BadRequest:
        return this.handleStatus400BadRequest(errorResponse);
      case StatusCodes.Status401Unauthorized:
        return this.handleStatus401Unauthorized(errorResponse);
      case StatusCodes.Status403Forbidden:
        return this.handleStatus403Forbidden(errorResponse);
      case StatusCodes.Status404NotFound:
        return this.handleStatus404NotFound(errorResponse);
      case StatusCodes.Status500InternalServerError:
        return this.handleStatus500InternalServerError(errorResponse);
      case StatusCodes.Status501NotImplemented:
        return this.handleStatus501NotImplemented(errorResponse);
      default:
        return this.handleOtherError(errorResponse);
    }
  }

  protected handleStatus400BadRequest(errorResponse: HttpErrorResponse): boolean {
    if (!errorResponse.error) {
      this.showMessage(
        Message.Error(
          'Request not understood. There may be a network issue. Please attempt to submit your request again. If this problem persists, please use the Report a Problem page found on the menu to alert your support team.',
        ),
      );
      return false;
    } else {
      if (typeof errorResponse.error === 'object') {
        this.showMessage(Message.Error(errorResponse.error.Message));
      }
      return true;
    }
  }

  protected getErrorsFromModelState(errorResponse: HttpErrorResponse): primeNg.Message[] {
    if (typeof errorResponse.error === 'object') {
      console.log(errorResponse.error['Message']);
      return [].concat(
        ...Object.keys(errorResponse.error).map(
          (key) => <string[]>errorResponse.error[key].map((err) => Message.Error(err)),
        ),
      );
    } else {
      return [Message.Error(errorResponse.error)];
    }
  }

  protected handleStatus401Unauthorized(errorResponse: HttpErrorResponse): boolean {
    this.showMessage(Message.Error('Your login session has timed out. Please log in again.'));
    return true;
  }

  protected handleStatus403Forbidden(errorResponse: HttpErrorResponse): boolean {
    this.showMessage(
      Message.Error(
        'You don’t have authorisation for this feature. Please refresh your page. If this problem persists, please use the Report a Problem page found on the menu to alert your support team.',
      ),
    );
    return false;
  }

  protected handleStatus404NotFound(errorResponse: HttpErrorResponse): boolean {
    return false;
  }

  protected handleStatus500InternalServerError(errorResponse: HttpErrorResponse): boolean {
    this.showMessage(
      Message.Error(
        'An error has occurred at the website and your support team will need to fix the problem. A preliminary report has been sent to the support team. Please do follow-up on the preliminary report using the Report a Problem page',
      ),
    );
    return false;
  }

  protected handleStatus501NotImplemented(errorResponse: HttpErrorResponse): boolean {
    return this.handleStatus500InternalServerError(errorResponse);
  }

  protected handleOtherError(errorResponse: HttpErrorResponse): boolean {
    return this.handleStatus500InternalServerError(errorResponse);
  }

  protected showMessage(message: primeNg.Message) {
    let messageService = this.injector.get(MessageService, null);

    if (messageService) {
      messageService.add(message);
    }
  }

  protected showMessages(messages: primeNg.Message[]) {
    let messageService = this.injector.get(MessageService, null);
    if (messageService) {
      messageService.addAll(messages);
    }
  }

  protected showDialog(message: string): Promise<any> {
    return new Promise((resolve, reject) => {
      let confirmationService = this.injector.get(ConfirmationService, null);
      if (confirmationService) {
        confirmationService.confirm({
          message: message,
          accept: () => {
            resolve(null);
          },
          reject: () => {
            reject();
          },
        });
      }
    });
  }
}

export class OnReadByIdResponsesHandler extends BaseHttpResponsesHandler {
  constructor(private entityTitle: string, injector: Injector) {
    super(injector);
  }

  protected handleStatus404NotFound(errorResponse: HttpErrorResponse): boolean {
    this.showMessage(
      Message.Error(
        `${this.entityTitle} not found. Perhaps another user has deleted the %s. Please consider refreshing the page.`,
      ),
    );
    return false;
  }
}

export class OnReadByFilterResponsesHandler extends BaseHttpResponsesHandler {
  protected handleStatus404NotFound(errorResponse: HttpErrorResponse): boolean {
    return this.handleStatus500InternalServerError(errorResponse);
  }
}

export class OnReadAllResponseHandler extends BaseHttpResponsesHandler {
  protected handleStatus404NotFound(errorResponse: HttpErrorResponse): boolean {
    return this.handleStatus500InternalServerError(errorResponse);
  }
}

export class OnCreateResponseHandler extends BaseHttpResponsesHandler {
  constructor(private entityTitle: string, injector: Injector) {
    super(injector);
  }

  handleSuccess(message?: string) {
    let msg = message != null && message != undefined ? message : `${this.entityTitle} saved.`;
    this.showMessage(Message.Success(msg));
  }

  protected handleStatus404NotFound(errorResponse: HttpErrorResponse): boolean {
    return true;
  }
}

export class OnUpdateResponseHandler extends BaseHttpResponsesHandler {
  constructor(private entityTitle: string, injector: Injector) {
    super(injector);
  }

  handleSuccess(message?: string) {
    let msg = message != null && message != undefined ? message : `${this.entityTitle} updated.`;
    this.showMessage(Message.Success(msg));
  }

  protected handleStatus404NotFound(errorResponse: HttpErrorResponse): boolean {
    this.showMessage(
      Message.Error(
        `${this.entityTitle} not found. Perhaps another user has deleted the ${this.entityTitle}. Please consider taking a note of your changes and then refreshing the page.`,
      ),
    );
    return false;
  }
}

export class OnDeleteResponseHandler extends BaseHttpResponsesHandler {
  constructor(private entityTitle: string, injector: Injector) {
    super(injector);
  }

  handleSuccess(message?: string) {
    let msg = message != null && message != undefined ? message : `${this.entityTitle} deleted.`;
    this.showMessage(Message.Success(msg));
  }

  protected handleStatus404NotFound(errorResponse: HttpErrorResponse): boolean {
    return false;
  }
}

@Injectable({ providedIn: 'root' })
export class HttpResponsesHandlersFactory {
  constructor(private injector: Injector) {}

  getDefault(): IHttpResponsesHandler {
    return new OnReadByFilterResponsesHandler(this.injector);
  }

  getForReadById(entityTitle: string): IHttpResponsesHandler {
    return new OnReadByIdResponsesHandler(entityTitle, this.injector);
  }

  getForReadByFilter(): IHttpResponsesHandler {
    return new OnReadByFilterResponsesHandler(this.injector);
  }

  getForReadAll(): IHttpResponsesHandler {
    return new OnReadAllResponseHandler(this.injector);
  }

  getForCreate(entityTitle: string): IHttpResponsesHandler {
    return new OnCreateResponseHandler(entityTitle, this.injector);
  }

  getForUpdate(entityTitle: string): IHttpResponsesHandler {
    return new OnUpdateResponseHandler(entityTitle, this.injector);
  }

  getForDelete(entityTitle: string): IHttpResponsesHandler {
    return new OnDeleteResponseHandler(entityTitle, this.injector);
  }
}
